import { useTourStore } from '@ice-web-app/shared-store';
import { Box, SxProps, Theme } from '@mui/material';
import * as React from 'react';
import { useEffect, useState } from 'react';
import * as ReactDOM from 'react-dom';
import { useLocation } from 'react-router-dom';
import { baseMaskString, basePortalString, baseTooltipContainerString, tourDefaultProps } from '../constants';
import { useDetectVisibility, useUpdateTour } from '../hooks';
import { TourLogic, TourOptions, TourProps, TourStep } from '../types';
import {
  getTargetInfo,
  getValidPortalRoot,
  getNearestScrollAncestor as utilGetNearestScrollAncestor,
} from '../utils/dom';
import { OrientationCoords } from '../utils/positioning';
import { getIdString } from '../utils/tour';
import { Mask } from './Mask';
import { Tip } from './Tip';
import { Tooltip } from './Tooltip';

const getNearestScrollAncestor = utilGetNearestScrollAncestor as any;

export const Tour = (props: TourProps) => {
  const { initialStepIndex, isOpen } = props;

  const [steps, setSteps] = useState<(TourStep & { isVisible?: boolean })[]>(
    props.steps.map((step) => ({ ...step, isVisible: false })) || [],
  );
  const [target, setTarget] = React.useState<HTMLElement | undefined>(undefined);
  const [tooltipPosition, setTooltipPosition] = React.useState<OrientationCoords | undefined>(undefined);
  const [currentStepIndex, setCurrentStepIndex] = React.useState<number>(initialStepIndex || 0);
  const [tourRoot, setTourRoot] = React.useState<Element | undefined>(undefined);

  const { setTourProps } = useTourStore();

  const cleanupRefs = React.useRef<Array<() => void>>([]);
  const tooltip = React.useRef<HTMLElement | undefined>(undefined);
  const portal = React.useRef<HTMLElement | undefined>(undefined);

  const currentStepContent: TourStep = steps[currentStepIndex];
  const tourOpen: boolean = isOpen || false;

  const options: TourOptions & TourProps & TourStep = {
    ...tourDefaultProps,
    ...props,
    ...currentStepContent,
  };

  const {
    maskPadding,
    maskRadius,
    tooltipMaxWidth,
    disableMaskInteraction,
    disableCloseOnClick,
    transition,
    customTooltipRenderer,
    zIndex,
    tooltipContainerSx,
    corner,
    rootSelector,
    customNextFunc,
    customPrevFunc,
    customCloseFunc,
    disableClose,
    disableNext,
    disablePrev,
    identifier,
    disableMask,
    renderMask,
  } = options;

  useEffect(() => {
    setSteps(props.steps);
  }, [props.steps]);

  React.useEffect(() => {
    return cleanup;
  }, []);

  const cleanup = () => {
    cleanupRefs.current.forEach((f) => f());
    cleanupRefs.current = [];
  };

  const goToStep = (stepIndex: number) => {
    if (stepIndex >= steps.length || stepIndex < 0) {
      return;
    }
    setCurrentStepIndex(stepIndex);
  };

  const checkTargetVisibility = (stepIndex: number) => {
    return !!window.document.querySelector(steps?.[stepIndex]?.selector);
  };

  // Start by looking at the next possible step
  let nextCounter = currentStepIndex + 1;

  // "Next" button action
  function handleNextClick() {
    const isPresent = checkTargetVisibility(nextCounter);
    // While we haven't reached the end, check if it's visible
    // If not visible, keep skipping ahead by one
    if (nextCounter < steps.length && !isPresent) {
      nextCounter = nextCounter + 1;
      handleNextClick();
    }

    // If we've found a visible step (index still valid), go to it
    if (nextCounter < steps.length) {
      goToStep(nextCounter);
    } else {
    }
  }

  // Start by looking at the next possible step
  let skipCounter = currentStepIndex + 1;
  let skipExtraCounter = skipCounter + 1;

  function handleSkipClick() {
    const isPresent = checkTargetVisibility(skipCounter);
    // While we haven't reached the end, check if it's visible
    // If not visible, keep skipping ahead by one
    if (skipCounter < steps.length && !isPresent) {
      skipCounter = skipCounter + 1;
      handleSkipClick();
    }

    if (skipExtraCounter < steps.length && !isPresent) {
      skipExtraCounter = skipExtraCounter + 1;
      handleSkipClick();
    }

    // If we've found a visible step (index still valid), go to it
    if (skipExtraCounter < steps.length) {
      goToStep(skipExtraCounter);
    } else {
    }
  }

  let prevCounter = currentStepIndex - 1;
  // "Prev" button action
  function handlePrevClick() {
    // Start by looking at the next possible step

    const isPresent = checkTargetVisibility(prevCounter);

    // While we haven't reached the end, check if it's visible
    // If not visible, keep skipping ahead by one
    if (prevCounter >= 0 && !isPresent) {
      prevCounter = prevCounter - 1;
      handlePrevClick();
    }

    // If we've found a visible step (index still valid), go to it
    if (prevCounter >= 0) {
      goToStep(prevCounter);
    } else {
    }
  }

  const baseLogic: TourLogic = {
    next: handleNextClick,
    skip: handleSkipClick,
    prev: handlePrevClick,
    close: (reset?: boolean) => closeTour(reset),
    goToStep: goToStep,
    stepContent: { ...options }, //pass options in as well to expose any defaults that aren't specified
    stepIndex: currentStepIndex,
    allSteps: steps,
    tooltipPosition,
  };

  const tourLogic: TourLogic = {
    ...baseLogic,
    ...(customNextFunc && {
      next: (fromTarget?: boolean) => {
        customNextFunc(baseLogic, fromTarget);
      },
    }),
    ...(customPrevFunc && {
      prev: () => {
        customPrevFunc(baseLogic);
      },
    }),
    ...(customCloseFunc && {
      close: () => {
        customCloseFunc(baseLogic);
      },
    }),
  };

  React.useEffect(() => {
    let root = rootSelector ? document.querySelector(rootSelector) : undefined;

    if (!root) {
      root = getNearestScrollAncestor(portal.current);
    }

    if (tourOpen !== false && root !== tourRoot) {
      setTourRoot(root as Element);
    }
  }, [rootSelector, portal.current, tourOpen]);

  // Disable scrolling when the tour is open
  React.useEffect(() => {
    if (tourOpen) {
      document.body.style.overflow = 'hidden'; // Disable page scrolling
    } else {
      document.body.style.overflow = ''; // Restore scrolling
    }

    // Cleanup on unmount or tour close
    return () => {
      document.body.style.overflow = '';
    };
  }, [tourOpen]);

  const { updateTour } = useUpdateTour({
    options,
    tourOpen,
    tooltip,
    cleanup,
    tourLogic,
    tourRoot,
    setTooltipPosition,
    currentStepIndex,
    currentStepContent,
    setTarget,
    cleanupRefs,
    target,
  });

  useDetectVisibility({
    selectors: steps.map((step) => step.selector),
    onVisible: (selector) => {
      if (!isOpen) return;
      setSteps((perv) => [
        ...perv.map((step) => {
          if (step.selector === selector) {
            return { ...step, isVisible: true };
          }
          return step;
        }),
      ]);
    },
    onHidden: (selector) => {
      if (!isOpen) return;
      setSteps((perv) => [
        ...perv.map((step) => {
          if (step.selector === selector) {
            return { ...step, isVisible: false };
          }
          return step;
        }),
      ]);
    },
    root: tourRoot,
  });

  const location = useLocation();

  useEffect(() => {
    goToStep(0);
  }, [location.pathname]);

  const closeTour = (reset?: boolean) => {
    if (reset) {
      goToStep(0);
    }

    setTourProps({ isOpen: false });
    cleanup();
    target?.focus(); // return focus to last target when closed
  };

  //don't render if the tour is hidden or if there's no step data
  if (!tourOpen || !currentStepContent) {
    return null;
  }

  const MaskTag = renderMask ? renderMask : Mask;

  const keyPressHandler = (event: React.KeyboardEvent) => {
    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        if (!disableClose) {
          tourLogic.close();
        }
        break;
      case 'ArrowRight':
        event.preventDefault();
        if (!disableNext) {
          tourLogic.next();
        }
        break;
      case 'ArrowLeft':
        event.preventDefault();
        if (!disablePrev) {
          tourLogic.prev();
        }
        break;
    }
  };

  const tooltipContainerStyle: SxProps<Theme> = {
    position: 'absolute',
    top: tooltipPosition?.coords.y,
    left: tooltipPosition?.coords.x,
    transition: transition,
    zIndex: 10000,
    pointerEvents: 'auto',
    ...(!customTooltipRenderer && {
      maxWidth: tooltipMaxWidth || { xs: 300, sm: 430, md: 480, lg: 530, xl: 580 },
      width: '100%',
      ...(!(corner === 'none') && {
        px: tourLogic.stepContent.corner === 'small' ? 7.5 : 0,
        py: tourLogic.stepContent.corner === 'small' ? 3.75 : 0,
      }),
    }),
    ...tooltipContainerSx,
  };

  const portalStyle: SxProps<Theme> = {
    position: 'fixed',
    overflow: 'hidden',
    top: 0,
    left: 0,
    zIndex: zIndex,
    visibility: 'visible',
    pointerEvents: 'none',
  };

  // render mask, tooltip, and their shared "portal" container
  const render = () => (
    <Box ref={portal} id={getIdString(basePortalString, identifier)} sx={portalStyle}>
      {tourRoot && (
        <MaskTag
          maskId={getIdString(baseMaskString, identifier)}
          targetInfo={getTargetInfo(tourRoot, target)}
          disableMaskInteraction={disableMaskInteraction}
          disableCloseOnClick={disableCloseOnClick}
          padding={maskPadding || 0}
          radius={maskRadius || 0}
          tourRoot={tourRoot}
          close={tourLogic.close}
          disableMask={disableMask}
        />
      )}

      {tourRoot && (
        <Box
          ref={tooltip}
          id={getIdString(baseTooltipContainerString, identifier)}
          sx={tooltipContainerStyle}
          onKeyDown={keyPressHandler}
        >
          {customTooltipRenderer ? customTooltipRenderer(tourLogic) : <Tooltip {...tourLogic} tooltipRef={tooltip} />}
        </Box>
      )}

      {tourRoot &&
        steps.map((step, index) => (
          <Tip
            key={index}
            step={step}
            index={index}
            isVisible={step.isVisible}
            goToStep={goToStep}
            activeIndex={currentStepIndex}
            containerRoot={tourRoot}
            // or if you prefer, pass the same root used by your Tour
          />
        ))}
    </Box>
  );

  // on first render, put everything in its normal context.
  // after first render (once we've determined the tour root) spawn a portal there for rendering.
  return tourRoot ? ReactDOM.createPortal(render(), getValidPortalRoot(tourRoot)) : render();
};
